import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import circlesBg from 'assets/svg/circles-bg.svg'
import * as SvgIcons from 'assets/svg/sprite'
import { IconButton } from 'components/IconButton/IconButton'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import InlineVideoEditor from 'components/UI/Modal/EditVideo/InlineVideoEditor'
import Sidebar from 'components/UI/Sidebar/Sidebar'
import SidebarItem from 'components/UI/Sidebar/SidebarItem'
import {
  ReelAddPoll,
  ScheduledInfoBubble,
  ShareReel,
  UploadReelMedia
} from 'components-ref/molecules'
import { useModalContext } from 'context/modalContext'
import DraggableTextContainer from 'features/PhotoEdit/DraggableText/DraggableTextContainer'
import ProcessingFile from 'features/ProcessingFile/ProcessingFile'
import { AllIcons } from 'helpers/allIcons'
import { useCreateReel } from 'services/reels'
import { useUserProfile } from 'store'

import { PollPreview } from '../newPost/components/PollPreview'

export const NewReel = () => {
  const [thumbnail, setThumbnail] = useState<string | null>(null)
  const [playerReady, setPlayerReady] = useState(false)
  const [lastPct, setLastPct] = useState(0)

  const { t } = useTranslation()
  const userData = useUserProfile()
  const modalData = useModalContext()
  const navigate = useNavigate()
  const payoutModalOpenedRef = useRef(false)
  const uploadCancelledRef = useRef(false)
  const {
    image,
    addPollOpen,
    setAddPollOpen,
    video,

    isAddTextOpen,
    initialPosition,
    initialText,
    handleSetCoordinates,
    pollData,
    handleRemoveText,
    isTextDragged,
    textValue,
    setTextValue,
    setIsTextDragged,

    scheduleDate,
    handleSetImageData,
    handleSetVideoData,
    reelPayload,
    reelReady,
    handleUploadAbort,
    handleSettingsClick,
    handleCloseClick,
    handleSetProgressInfo,
    toggleDateModal,
    uploadProgress,
    handleSetPollFunction,
    handleRemoveSchedule,
    handleClearPollData,
    handleOpenPoll,
    handleOpenText,
    clearMedia,
    mediaType,
    setMediaType
  } = useCreateReel()

  const hasVideo = Boolean(video?.length)
  const hasImage = Boolean(image)
  const hasMedia = hasVideo || hasImage

    const payoutDisabled =
        userData?.role === 'model' &&
        userData?.payout_method == null

const shareDisabled = useMemo(() => {
  const isUploading =
    uploadProgress !== null &&
    typeof uploadProgress?.percentage === 'number' &&
    uploadProgress.percentage < 100

  return payoutDisabled || isUploading || !reelReady
}, [payoutDisabled, uploadProgress, reelReady])

const isUploading = useMemo(() => {
  const pct = uploadProgress?.percentage

  return (
    hasVideo &&
    (
      mediaType?.type?.includes('video') ||
      typeof pct === 'number'
    ) &&
    (typeof pct !== 'number' || pct < 100)
  )
}, [hasVideo, mediaType?.type, uploadProgress])


  useEffect(() => {
    const p = uploadProgress?.percentage

    if (typeof p === 'number' && p >= 0 && p <= 100) {
      setLastPct((prev) => (p >= prev ? p : prev))
    }
  }, [uploadProgress?.percentage])

  const blurBgSrc = useMemo(() => {
    return thumbnail ?? image?.path ?? null
  }, [thumbnail, image?.path])

  useEffect(() => {
    setPlayerReady(false)
  }, [video?.[0]?.s3_path])

  useEffect(() => {
    if (!userData) return

    if (payoutDisabled && !payoutModalOpenedRef.current) {
      payoutModalOpenedRef.current = true

      modalData.addModal(
        t('payoutRequired'),
        <ConfirmModal
          confirmButtonWidth="14"
          body={t('payoutRequiredDescription')}
          confirmText={t('gotoSettings')}
          confirmFn={() => {
            navigate('/settings/general/payout-settings')
          }}
        />
      )
    }
  }, [userData, payoutDisabled, modalData, t, navigate])

  return (
    <ReelWrapper>
      {!addPollOpen && <Title>{t('addReel')}</Title>}

      {Boolean(image) && (
        <MediaWrapper
          style={{
            backgroundImage: `url(${image?.path})`
          }}
        >
          <MediaContainer
            style={{
              backgroundImage: `url(${image?.path})`
            }}
          />
        </MediaWrapper>
      )}

      {Boolean(video?.length) && (
        <MediaWrapper style={{ backgroundColor: 'black' }}>
          <MediaContainer
            style={{
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none'
            }}
          >
            {blurBgSrc && (
              <div
                className="preview__background"
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `url(${blurBgSrc})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
filter: 'blur(20px) brightness(0.84)',
transform: 'none',
zIndex: 0,
                  pointerEvents: 'none'
                }}
              />
            )}

            {!playerReady && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 0,
                  pointerEvents: 'none'
                }}
              >
                {thumbnail ? (
                  <img
                    src={thumbnail}
                    alt="thumbnail"
                    style={{
                      display: 'block',
                      width: 'auto',
                      height: 'auto',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.35)',
                      borderRadius: '8px'
                    }}
                  />
                ) : (
                  <div style={{ color: 'white', fontSize: 12, opacity: 0.8 }}>
                    (no thumbnail; waiting for player)
                  </div>
                )}
              </div>
            )}


<ReelVideoContainer>
  <InlineVideoEditor
    vidSrc={video?.[0]?.s3_path ?? video?.[0]?.path ?? ''}
    storageSrc={video?.[0]?.path ?? video?.[0]?.s3_path ?? ''}
    thumbnails={[]}
    orientation={video?.[0]?.orientation}
    start={video?.[0]?.trim_start ?? 0}
    end={video?.[0]?.trim_end ?? undefined}
    maxDuration={60}
    hidePlayButton={isUploading}
    isUploading={isUploading}
    onRangeChange={({ startTime, endTime }) => {
      const firstVideo = video?.[0]

      if (!firstVideo) return

      handleSetVideoData([
        {
          ...firstVideo,
          trim_start: startTime,
          trim_end: endTime,
          video_exif:
            typeof firstVideo.video_exif === 'string'
              ? firstVideo.video_exif
              : JSON.stringify(firstVideo.video_exif ?? {})
        }
      ])
    }}
  />
</ReelVideoContainer>
          </MediaContainer>
        </MediaWrapper>
      )}

      {(textValue || isAddTextOpen) && (
        <TextContainer>
          <DraggableTextContainer
            customClass="photoEdit__container__text"
            initialValue={initialText}
            initialX={initialPosition[0]}
            initialY={initialPosition[1]}
            isEditable={true}
            isOpen={isAddTextOpen}
            isDragging={isTextDragged}
            setValue={(v) => {
              setTextValue(v)
            }}
            setCoordinates={(x, y) => {
              handleSetCoordinates(x, y)
            }}
            setIsDragging={(d) => {
              setIsTextDragged(d)
            }}
            onRemove={() => {
              handleRemoveText()
            }}
          />
        </TextContainer>
      )}

      {pollData && (
        <PollContainer>
          <PollPreview
            pollData={pollData}
            clearPollData={() => {
              handleClearPollData()
            }}
          />
        </PollContainer>
      )}

      {!addPollOpen && (
        <ReelSidebar>
          <Sidebar>
              {!hasVideo && !pollData && (
              <SidebarItem
                icon={<SvgIcons.IconText color="white" />}
                text={t('text')}
                isDisabled={isAddTextOpen}
                onClick={() => {
                  handleOpenText()
                }}
              />
            )}

{/*           {!hasVideo && !pollData && (
            <SidebarItem
              icon={<SvgIcons.IconPoll color="white" />}
              text={t('poll')}
              isDisabled={!hasMedia || Boolean(pollData)}
              onClick={() => {
                if (!hasMedia || pollData) return
                handleOpenPoll()
              }}
            />
          )} */}

            {/* Add future sidebar items here. They can still show even when hasVideo is true. */}
          </Sidebar>
        </ReelSidebar>
      )}

      <CloseButtonWrapper>
        <IconButton
          desc={t('close')}
          type="transparent"
          size="small"
          clickFn={() => {
            handleCloseClick()
          }}
          icon={AllIcons.close}
        />
      </CloseButtonWrapper>

      <FooterContainer>
        {scheduleDate && (
          <UpperFooter>
            <ScheduledInfoBubble
              date={scheduleDate}
              removeSchedule={() => {
                handleRemoveSchedule()
              }}
            />
          </UpperFooter>
        )}

        <LowerFooter>
          <UploadReelMedia
            disabled={payoutDisabled}
setImageData={(
  path,
  orientation,
  exifData,
  storagePath,
  thumbPath,
  id
) => {
  if (uploadCancelledRef.current) return

  handleSetImageData(
    path,
    orientation,
    exifData,
    storagePath,
    thumbPath,
    id
  )
}}
setVideoData={(data) => {
  if (uploadCancelledRef.current) return
  handleSetVideoData(data)
}}
setProgressInfo={(p) => {
  if (uploadCancelledRef.current) return
  handleSetProgressInfo(p)
}}
            clearMedia={() => {
              clearMedia()
            }}
setMediaType={(file) => {
  uploadCancelledRef.current = false
  setMediaType(file)
}}
setThumbnail={(thumb) => {
  if (uploadCancelledRef.current) return
  setThumbnail(thumb)
}}
          />

          {isUploading ? (
<CancelUploadButton
  type="button"
  onClick={() => {
    uploadCancelledRef.current = true
    handleUploadAbort()
    clearMedia()
    setThumbnail(null)
    setLastPct(0)
    setPlayerReady(false)
  }}
>
  Cancel Upload
</CancelUploadButton>
) : (
  <ShareReel reel={reelPayload} disabled={shareDisabled} />
)}

          <FooterButton
            clickFn={() => {
              toggleDateModal()
            }}
            icon={
              <SvgIcons.IconCalendarClockOutline2
                color={scheduleDate ? '#2894FF' : 'white'}
              />
            }
            type="black--transparent"
          />
        </LowerFooter>
      </FooterContainer>

{isUploading && (
  <UploadProgressContainer
    percentage={lastPct || 1}
    onAbort={handleUploadAbort}
    text="Uploading"
    hideAbort
  />
)}
 
      <ReelAddPoll
        open={addPollOpen}
        setOpen={(open) => {
          setAddPollOpen(open)
        }}
        setPollDataFunction={(poll) => {
          handleSetPollFunction(poll)
        }}
      />
    </ReelWrapper>
  )
}
const ReelVideoContainer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;

  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;

  overflow: hidden;
  background: transparent;

  /*
   * Only affect InlineVideoEditor when rendered
   * inside NewReel.
   */
  .editVideoModal--inline {
    position: absolute;
    inset: 0;

    width: 100%;
    height: 100%;

    overflow: hidden;
    background: transparent;
  }

  /*
   * Keep the video player filling the reel area.
   */
  .editVideoModal__player {
    position: absolute;
    inset: 0;

    width: 100%;
    height: 100%;
  }

  .editVideoModal__player__wrapper {
    width: 100%;
    height: 100%;

    display: flex;
    align-items: center;
    justify-content: center;

    overflow: hidden;
  }

  .editVideoModal__player__video {
    max-width: 100%;
    max-height: 100%;
  }

  video {
    display: block;

    max-width: 100%;
    max-height: 100%;

    object-fit: contain;
    object-position: center;

    background: transparent;
  }

  /*
   * Anchor the trimming timeline near the bottom
   * of the reel, above the footer buttons.
   */
.editVideoModal--inline .editVideoModal__slider {
  position: absolute;

  top: auto;
  bottom: 10rem;

  left: 2rem;
  right: 2rem;

  width: auto;
  height: 4rem;

  margin: 0;
  z-index: 20;
}

  /*
   * Keep the timeline timer directly above
   * the thumbnails.
   */
  .editVideoModal--inline .editVideoModal__slider__timer {
    position: absolute;
    top: -3rem;
    left: 50%;

    transform: translateX(-50%);
  }

  /*
   * Preserve the existing trim handles and
   * thumbnail alignment.
   */
  .editVideoModal--inline .editVideoModal__slider__wrapper {
    top: 0;
    bottom: 0;
  }
`
const FooterContainer = styled.div`
  width: calc(100% - 40px);
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 0;
  margin: 0 20px 2.7rem;
  z-index: 1;
  pointer-events: auto;
`

const UpperFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`

const LowerFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-around;
  align-items: center;
  z-index: 1;

  @media (min-width: 800px) {
    justify-content: center;
    gap: 40px;
  }
`

const SettingsButtonWrapper = styled.div`
  position: absolute;
  top: 3rem;
  left: 2rem;

  background: none;
  border: none;

  padding: 0;
`

const PollContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: center;
  align-items: flex-end;
  flex: 1 1;

  position: absolute;
  bottom: 13rem;
  z-index: 1;
`

const ReelSidebar = styled.div`
  position: absolute;
  top: 50%;
  left: 1rem;
  z-index: 2;
  transform: translateY(-50%);
`

const TextContainer = styled.div`
  height: 100%;
  width: 100%;

  position: absolute;
  top: 0;
  left: 0;

  overflow: hidden;
`

const MediaWrapper = styled.div`
  position: relative;

  width: 100%;
  height: 100%;

  overflow: hidden;
  isolation: isolate;

  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;

  @media (min-width: 768px) {
    border-radius: 2.5rem;
  }
`
const MediaContainer = styled.div`
  position: absolute;
  inset: 0;

  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;

  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;

  backdrop-filter: blur(19px) brightness(0.8);
  -webkit-backdrop-filter: blur(19px) brightness(0.8);

  @media (min-width: 768px) {
    border-radius: 2.5rem;
  }
`
const Title = styled.div`
  position: absolute;
  left: 50%;
  z-index: 3;

  font-family: 'Montserrat', sans-serif;
  color: white;
  font-style: normal;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.02em;

  transform: translate(-50%);

  margin-top: 26px;
`

const ReelWrapper = styled.div`
  height: 100dvh;
  position: relative;
  overflow: hidden;

  background-image:
    url(${circlesBg}),
    linear-gradient(157.59deg, #181920 0%, #0f0f13 100%);
  background-position: center 16%, center center;
  background-repeat: no-repeat, no-repeat;
  background-size: 90rem auto, cover;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    @media (min-width: 768px) {
      border-radius: 2.5rem;
    }
    background: linear-gradient(
      218.53deg,
      rgba(6, 48, 90, 0.65) 16.4%,
      rgba(15, 80, 180, 0.25) 45%,
      rgba(0, 0, 0, 0) 83.32%
    );
  }

  @media (min-width: 768px) {
    width: 100%;
    height: calc(100dvh - 9rem);

    border-radius: 2.5rem;
    overflow: hidden;
  }
`
type FooterButtonProps = {
  disabled?: boolean
}

export const FooterButton = styled(IconButton)<FooterButtonProps>`
  border: none;
  border-radius: 2rem;

  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`

const UploadProgressContainer = styled(ProcessingFile)`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 5;

  transform: translate(-50%, -50%);

  width: 12rem;
  height: auto;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  pointer-events: none;

  .processingFile__wrapper {
    width: 100%;
    height: auto;
    padding: 0;

    background: none !important;
    opacity: 0.8 !important;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .processingFile__progress {
    position: relative;
    top: auto;
    right: auto;
    bottom: auto;
    left: auto;

    width: 100%;
    padding: 0;
    margin-top: 1rem;

    display: flex;
    flex-direction: column;
    align-items: center;

    color: white;
    text-align: center;
  }

  .processingFile__progress--bar {
    width: 100%;
  }

  button,
  .processingFile__close,
  .processingFile__remove,
  .processingFile__abort,
  .processingFile__cancel {
    display: none !important;
  }
`

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 3rem;
  right: 2rem;
  z-index: 3;

  @media (min-width: 768px) {
    display: none;
  }
`
const CancelUploadButton = styled.button`
  width: 16.5rem;
  height: 5rem;

  border: none;
  border-radius: 2.5rem;

  background: rgba(4, 4, 4, 0.55);
  color: #ffffff;

  font-family: 'Montserrat', sans-serif;
  font-size: 1.4rem;
  font-weight: 500;
  letter-spacing: 0.02em;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
`